// SceneManager — PIXI scene lifecycle Facade (map-architecture Phase 4, scene-state step 02).
//
// Owns Application / viewport / layers init + resume/pause lifecycle + texture
// cleanup. Mutable scene fields live on MapSceneState (passed as deps.scene);
// analytics once-flags / mapEnterTs / didInitialHouseCamera / decorAtlasSheet
// live there too — this class no longer bag-gets/sets them.
import posthog from 'posthog-js';
import { requestSceneRebuild } from '../../sceneHealth';
import * as PIXI from 'pixi.js';
import { Viewport } from 'pixi-viewport';
import * as Sentry from '@sentry/react';
import useInterfaceStore from '../../../stores/useInterfaceStore';
import { showAlert } from '../../../utils/telegram/alert';
import { mapSceneSingleton, ensurePlotMarkerTexture, PLOT_MARKER_TEXTURE_PATH } from '../mapScene';
import type { MapSceneState } from '../mapSceneState';
import { WORLD_SIZE, INITIAL_ZOOM, getMinZoomScale, applyClampZoom } from '../mapViewport';
import type { TickerBoundContainer } from '../render/mapPixiExtensions';

export interface SceneManagerDeps {
  scene: MapSceneState;
  setIsPixiAppReady: (value: boolean) => void;
  setLastRenderKey: (key: string | null) => void;
  mapLoadedTexturePaths: Set<string>;
  CLOUDS_OVERLAY_PATH: string;
  CLOUDS_FADE_START_SCALE: number;
  CLOUDS_FADE_END_SCALE: number;
  CLOUDS_MAX_ALPHA: number;
  MERCURY_X: number;
  MERCURY_Y: number;
}

export class SceneManager {
  private deps: SceneManagerDeps;

  // Mirrors the old effect's per-run `let isMounted`. Each mount() call creates a
  // fresh state object captured by that run's async closures; unmount() flips the
  // ACTIVE run's flag. A stale closure from a previous mount keeps seeing its own
  // (now false) flag, so a fast unmount→remount can never let old async work
  // resume against the new mount — byte-identical to the original hook semantics.
  private activeMount: { isMounted: boolean } | null = null;
  private canvasEl: HTMLDivElement | null = null;

  constructor(deps: SceneManagerDeps) {
    this.deps = deps;
  }

  /** Reset once-trackers so PostHog events fire again on next full map mount. */
  private resetMapTrackingState = (): void => {
    this.deps.scene.analytics.once.reset();
    this.deps.scene.analytics.mapEnterTs = 0;
  };

  /** Synchronously free all map textures from GPU, CPU, and caches. */
  private cleanupMapTextures = (): void => {
    const { mapLoadedTexturePaths } = this.deps;
    const loader = PIXI.Assets.loader;
    for (const texPath of mapLoadedTexturePaths) {
      try {
        // Assets.get defaults to any; narrow to the two shapes we actually free.
        const asset = PIXI.Assets.get(texPath) as PIXI.Spritesheet | PIXI.Texture | undefined;
        // A spritesheet (.json) asset is a PIXI.Spritesheet, not a Texture — its
        // GPU memory lives on the shared textureSource. Unload that, then destroy
        // the sheet so its per-frame Texture wrappers + the cache entry go away.
        if (asset instanceof PIXI.Spritesheet) {
          const firstFrame = Object.values(asset.textures || {})[0] as PIXI.Texture | undefined;
          const src = asset.textureSource || firstFrame?.source;
          if (src) {
            src.unload();
            if (src.resource && typeof src.resource.close === 'function') {
              src.resource.close();
            }
          }
          try { asset.destroy(true); } catch { /* ignore */ }
        } else if (asset?.source) {
          asset.source.unload(); // Free GPU
          if (asset.source.resource && typeof asset.source.resource.close === 'function') {
            asset.source.resource.close(); // Free decoded ImageBitmap CPU memory
          }
        }
      } catch { /* texture may already be gone */ }
      try { PIXI.Cache.remove(texPath); } catch { /* ignore */ }
      try {
        const absUrl = PIXI.path.toAbsolute(texPath);
        if (absUrl && loader.promiseCache[absUrl]) {
          delete loader.promiseCache[absUrl];
        }
      } catch { /* ignore */ }
    }
    mapLoadedTexturePaths.clear();
    // The atlas baseTexture was just unloaded (its path is in mapLoadedTexturePaths);
    // drop the stale Spritesheet handle so the next map preload re-loads it.
    this.deps.scene.assets.decorAtlasSheet = null;
  };

  private attachCanvas = (): void => {
    if (!mapSceneSingleton.app) return;
    if (mapSceneSingleton.app.canvas.parentElement) {
      mapSceneSingleton.app.canvas.parentElement.removeChild(mapSceneSingleton.app.canvas);
    }
    const canvasEl = this.canvasEl;
    if (!canvasEl) return;
    canvasEl.innerHTML = '';
    mapSceneSingleton.app.canvas.style.display = 'block';
    canvasEl.appendChild(mapSceneSingleton.app.canvas);

    // Заборона контекстного меню браузера та drag зображення
    const canvas = mapSceneSingleton.app.canvas;
    canvas.addEventListener('contextmenu', (e) => e.preventDefault());
    canvas.addEventListener('dragstart', (e) => e.preventDefault());
  };

  private attachResizeListener = (): void => {
    if (mapSceneSingleton.updateViewportSize && !mapSceneSingleton.isResizeListenerAttached) {
      window.addEventListener('resize', mapSceneSingleton.updateViewportSize);
      mapSceneSingleton.isResizeListenerAttached = true;
    }
  };

  private resumeApp = (): void => {
    if (!mapSceneSingleton.app) return;
    if (mapSceneSingleton.stageEventMode) {
      mapSceneSingleton.app.stage.eventMode = mapSceneSingleton.stageEventMode;
    }
    mapSceneSingleton.app.stage.interactiveChildren = true;
    mapSceneSingleton.app.ticker.start();
    if (mapSceneSingleton.viewport) {
      mapSceneSingleton.viewport.pause = false;
    }
    mapSceneSingleton.app.canvas.style.display = 'block';
  };

  private pauseApp = (): void => {
    if (!mapSceneSingleton.app) return;
    if (!mapSceneSingleton.stageEventMode) {
      mapSceneSingleton.stageEventMode = mapSceneSingleton.app.stage.eventMode ?? 'passive';
    }
    mapSceneSingleton.app.stage.eventMode = 'none';
    mapSceneSingleton.app.stage.interactiveChildren = false;
    mapSceneSingleton.app.ticker.stop();
    if (mapSceneSingleton.viewport) {
      mapSceneSingleton.viewport.pause = true;
    }
    mapSceneSingleton.app.canvas.style.display = 'none';
    if (mapSceneSingleton.app.canvas.parentElement) {
      mapSceneSingleton.app.canvas.parentElement.removeChild(mapSceneSingleton.app.canvas);
    }
  };

  private syncNewIslandMarkerListener = (): void => {
    const { scene } = this.deps;
    // ТИМЧАСОВИЙ КОД: закоментовано маркер нового острова
    const newIslandMarker = mapSceneSingleton.newIslandMarker;
    if (newIslandMarker) {
      // Видаляємо маркер, якщо він вже існує
      if (newIslandMarker.parent) {
        newIslandMarker.parent.removeChild(newIslandMarker);
      }
      newIslandMarker.destroy();
      mapSceneSingleton.newIslandMarker = null;
      scene.sprites.newIslandMarker = null;
    }
    // if (!newIslandMarker) return;
    // newIslandMarker.removeAllListeners('pointerdown');
    // newIslandMarker.on('pointerdown', (event: PIXI.FederatedPointerEvent) => {
    //   event.stopPropagation();
    //   setIsNewIslandModalOpen(true);
    // });
  };

  private clearMapMarkers = (): void => {
    const { scene } = this.deps;
    const { layers, markers, sprites, pixi } = scene;
    console.warn('[MapCanvas] clearMapMarkers() called — ALL markers will be destroyed', new Error().stack?.split('\n').slice(1, 5).join(' | '));
    const mapObjectsLayer = layers.mapObjects;
    if (!mapObjectsLayer) return;

    markers.plots.forEach((marker) => {
      mapObjectsLayer.removeChild(marker);
      marker.destroy();
    });
    markers.plots.clear();

    markers.realEstate.forEach((marker) => {
      mapObjectsLayer.removeChild(marker);
      marker.destroy();
    });
    markers.realEstate.clear();

    markers.mapObjects.forEach((marker) => {
      mapObjectsLayer.removeChild(marker);
      marker.destroy();
    });
    markers.mapObjects.clear();

    markers.sales.forEach((marker) => {
      if (marker.parent) marker.parent.removeChild(marker);
      marker.destroy();
    });
    markers.sales.clear();

    markers.avatars.forEach((marker) => {
      if (marker.parent) marker.parent.removeChild(marker);
      marker.destroy();
    });
    markers.avatars.clear();

    markers.quests.forEach((marker) => {
      mapObjectsLayer.removeChild(marker);
      marker.destroy();
    });
    markers.quests.clear();

    if (layers.island) {
      markers.islands.forEach((marker) => {
        layers.island!.removeChild(marker);
        marker.destroy();
      });
    }
    markers.islands.clear();

    if (sprites.mapObjectHighlight && layers.highlight) {
      const hl = sprites.mapObjectHighlight as TickerBoundContainer;
      if (pixi.app && hl.tickerFunction) {
        pixi.app.ticker.remove(hl.tickerFunction);
      }
      layers.highlight.removeChild(sprites.mapObjectHighlight);
      sprites.mapObjectHighlight.destroy();
      sprites.mapObjectHighlight = null;
      mapSceneSingleton.mapObjectHighlight = null;
    }
  };

  private initApp = async (mount: { isMounted: boolean }): Promise<void> => {
    const {
      scene, setIsPixiAppReady, mapLoadedTexturePaths,
      CLOUDS_OVERLAY_PATH, CLOUDS_FADE_START_SCALE, CLOUDS_FADE_END_SCALE,
      CLOUDS_MAX_ALPHA, MERCURY_X, MERCURY_Y,
    } = this.deps;
    const { pixi, layers, sprites, assets } = scene;

    // A phone reports devicePixelRatio 2-3, but PIXI sizes its drawing buffer
    // in CSS pixels by default: the whole scene was being rendered at a third
    // of the screen's real resolution and stretched back up by the browser.
    // That stretch is why the room reads soft on a phone and sharp on a
    // desktop, and — because it magnifies every sub-pixel sampling error by the
    // same factor — why the character and the furniture ripple like heat haze
    // while she walks or the camera pans, a wobble that is sub-pixel and
    // invisible at 1:1. Rendering at device pixels removes the upscale.
    //
    // Capped at 2 because the scene is fill-rate bound and every extra sample is
    // paid on every frame: 16.7 ms at 1x, 25.8 ms at 1.5x, 41.4 ms at 2x on the
    // software rasterizer at 390x844. The third step is past what the eye
    // resolves on a phone anyway.
    const resolution = Math.min(window.devicePixelRatio || 1, 2);
    const app = new PIXI.Application();
    await app.init({
      resizeTo: window,
      background: '#77C0AF', // Ocean color instead of black
      // No MSAA: the map is sprites (already smooth through mipmaps and linear
      // filtering); multisampling the whole screen cost phones fill-rate for nothing.
      antialias: false,
      resolution,
      autoDensity: true,
      preferWebGLVersion: 2,
    });

    mapSceneSingleton.app = app;
    pixi.app = app;
    if (!mapSceneSingleton.stageEventMode) {
      mapSceneSingleton.stageEventMode = app.stage.eventMode ?? 'passive';
    }

    // WebGL context loss recovery
    const canvas = app.canvas;
    canvas.addEventListener('webglcontextlost', (e) => {
      e.preventDefault();
      console.warn('[MapCanvas] WebGL context lost');
      Sentry.captureMessage('WebGL context lost', { level: 'warning' });
      try { posthog.capture('webgl_context_lost'); } catch { /* never block the game */ }
      if (mapSceneSingleton.app) {
        mapSceneSingleton.app.ticker.stop();
      }
    });
    canvas.addEventListener('webglcontextrestored', () => {
      console.info('[MapCanvas] WebGL context restored');
      Sentry.captureMessage('WebGL context restored', { level: 'info' });
      if (mapSceneSingleton.app) {
        mapSceneSingleton.app.ticker.start();
      }
      // Textures whose images were released cannot come back by themselves —
      // rebuild the scene that is on screen (map or room) from scratch.
      requestSceneRebuild('webgl_context_restored');
    });

    this.attachCanvas();

    const oceanSize = WORLD_SIZE;
    const oceanOffset = oceanSize / 2;
    const worldWidth = WORLD_SIZE;
    const worldHeight = WORLD_SIZE;

    const viewport = new Viewport({
      screenWidth: window.innerWidth,
      screenHeight: window.innerHeight,
      worldWidth: worldWidth,
      worldHeight: worldHeight,
      events: app.renderer.events,
    });

    viewport.pause = true;
    const initialZoom = Math.max(INITIAL_ZOOM, getMinZoomScale(window.innerWidth, window.innerHeight));
    viewport.setZoom(initialZoom);
    viewport.x = -MERCURY_X * initialZoom + window.innerWidth / 2;
    viewport.y = -MERCURY_Y * initialZoom + window.innerHeight / 2;

    app.stage.addChild(viewport);
    pixi.viewport = viewport;
    mapSceneSingleton.viewport = viewport;

    const backgroundLayer = new PIXI.Container();
    const islandLayer = new PIXI.Container();
    const highlightLayer = new PIXI.Container();
    const mapObjectsLayer = new PIXI.Container();
    const cloudsLayer = new PIXI.Container();

    mapObjectsLayer.sortableChildren = true;
    mapObjectsLayer.cullableChildren = true;
    // Render groups: moving the camera then moves each layer as a whole on the
    // GPU instead of recomputing ~1100 object transforms every frame (the top
    // PIXI cost of panning in tools/profiling).
    islandLayer.isRenderGroup = true;
    mapObjectsLayer.isRenderGroup = true;

    viewport.addChild(backgroundLayer);
    viewport.addChild(islandLayer);
    viewport.addChild(highlightLayer);
    viewport.addChild(mapObjectsLayer);
    viewport.addChild(cloudsLayer);

    layers.mapObjects = mapObjectsLayer;
    layers.highlight = highlightLayer;
    layers.clouds = cloudsLayer;
    layers.island = islandLayer;
    mapSceneSingleton.mapObjectsLayer = mapObjectsLayer;
    mapSceneSingleton.highlightLayer = highlightLayer;
    mapSceneSingleton.cloudsLayer = cloudsLayer;
    mapSceneSingleton.islandLayer = islandLayer;

    const ocean = new PIXI.Graphics();
    ocean.rect(-oceanOffset, -oceanOffset, oceanSize, oceanSize).fill(0x77C0AF);
    backgroundLayer.addChild(ocean);

    // Both requested at once (the splash has usually preloaded them, mapPreload.ts).
    const cloudsTexturePromise = PIXI.Assets.load<PIXI.Texture>(CLOUDS_OVERLAY_PATH);
    cloudsTexturePromise.catch(() => { /* reported below */ });

    try {
      // Single loader for this asset — ensurePlotMarkerTexture owns the path, the
      // mipmap/scale setup and the in-flight memo, so no init path can leave the
      // singleton null while markers are being built.
      assets.plotMarkerTexture = await ensurePlotMarkerTexture();
      mapLoadedTexturePaths.add(PLOT_MARKER_TEXTURE_PATH);

      // ТИМЧАСОВИЙ КОД: закоментовано маркер нового острова
      // if (plotMarkerTextureRef.current && mapObjectsLayerRef.current) {
      //   const newIslandMarker = new PIXI.Container();
      //   const markerSprite = new PIXI.Sprite(plotMarkerTextureRef.current);
      //   markerSprite.width = PLOT_MARKER_SIZE.width;
      //   markerSprite.height = PLOT_MARKER_SIZE.height;
      //   markerSprite.anchor.set(0.5, 1.0);
      //   newIslandMarker.addChild(markerSprite);
      //
      //   newIslandMarker.position.set(-257.7367344797319, 459.48201264026875);
      //   newIslandMarker.zIndex = 459.48201264026875;
      //   newIslandMarker.eventMode = 'static';
      //   newIslandMarker.cursor = 'pointer';
      //
      //   newIslandMarkerRef.current = newIslandMarker;
      //   mapSceneSingleton.newIslandMarker = newIslandMarker;
      //   syncNewIslandMarkerListener();
      //
      //   mapObjectsLayerRef.current.addChild(newIslandMarker);
      // }
    } catch (error) {
      console.error('[MapCanvas] Failed to load plot marker texture:', error);
    }

    try {
      const cloudsTexture = await cloudsTexturePromise;
      mapLoadedTexturePaths.add(CLOUDS_OVERLAY_PATH);
      const cloudsSprite = new PIXI.Sprite(cloudsTexture);
      cloudsSprite.anchor.set(0.5);
      cloudsSprite.position.set(0, 0);
      cloudsSprite.width = WORLD_SIZE;
      cloudsSprite.height = WORLD_SIZE;
      cloudsSprite.alpha = 0;
      cloudsSprite.eventMode = 'none';

      cloudsLayer.addChild(cloudsSprite);
      sprites.clouds = cloudsSprite;
      mapSceneSingleton.cloudsSprite = cloudsSprite;

      const updateCloudsAlpha = () => {
        if (!pixi.viewport || !sprites.clouds) return;
        const scale = pixi.viewport.scale.x;
        const t = (CLOUDS_FADE_START_SCALE - scale) / (CLOUDS_FADE_START_SCALE - CLOUDS_FADE_END_SCALE);
        const clamped = Math.max(0, Math.min(1, t));
        const targetAlpha = clamped * CLOUDS_MAX_ALPHA;
        sprites.clouds.alpha += (targetAlpha - sprites.clouds.alpha) * 0.08;
      };

      pixi.app?.ticker.add(updateCloudsAlpha);
      (cloudsSprite as TickerBoundContainer).tickerFunction = updateCloudsAlpha;
    } catch (error) {
      console.error('[MapCanvas] Failed to load clouds overlay texture:', error);
    }

    const updateViewportSize = () => {
      if (pixi.viewport) {
        pixi.viewport.screenWidth = window.innerWidth;
        pixi.viewport.screenHeight = window.innerHeight;
        applyClampZoom(pixi.viewport);
      }
    };
    pixi.updateViewportSize = updateViewportSize;
    mapSceneSingleton.updateViewportSize = updateViewportSize;
    this.attachResizeListener();

    viewport.drag({ wheel: true }).pinch().wheel().decelerate();
    // While the camera is being dragged or pinched nothing on the map can be
    // tapped — skip hit-testing the whole object tree on every pointer move.
    // (drag-start only fires past the move threshold, so taps are unaffected.)
    const setObjectHitTesting = (on: boolean) => {
      mapObjectsLayer.interactiveChildren = on;
      islandLayer.interactiveChildren = on;
    };
    viewport.on('drag-start', () => setObjectHitTesting(false));
    viewport.on('drag-end', () => setObjectHitTesting(true));
    viewport.on('pinch-start', () => setObjectHitTesting(false));
    viewport.on('pinch-end', () => setObjectHitTesting(true));
    applyClampZoom(viewport);
    // IBounceOptions.bounceBox is typed as PIXI.Rectangle; values match the
    // plain {x,y,width,height} bag bounce reads at runtime.
    const bounceBox = new PIXI.Rectangle(-oceanOffset, -oceanOffset, oceanSize, oceanSize);

    viewport
      .bounce({
        bounceBox,
        sides: 'all',
        friction: 0.5,
        time: 150,
        ease: 'easeInOutSine',
        underflow: 'none',
      });

    viewport.moveCenter(MERCURY_X, MERCURY_Y);
    viewport.pause = false;

    mapSceneSingleton.isReady = true;
    mapSceneSingleton.initPromise = null;

    if (mount.isMounted) {
      setIsPixiAppReady(true);
      this.resumeApp();
    }
  };

  mount(canvasEl: HTMLDivElement | null): void {
    if (!canvasEl) return;
    this.canvasEl = canvasEl;

    const mountState = { isMounted: true };
    this.activeMount = mountState;

    const {
      scene, setIsPixiAppReady, mapLoadedTexturePaths,
      CLOUDS_OVERLAY_PATH,
    } = this.deps;
    const { pixi, layers, sprites, assets } = scene;

    if (mapSceneSingleton.app && mapSceneSingleton.isReady) {
      pixi.app = mapSceneSingleton.app;
      pixi.viewport = mapSceneSingleton.viewport;
      layers.mapObjects = mapSceneSingleton.mapObjectsLayer;
      layers.highlight = mapSceneSingleton.highlightLayer;
      layers.clouds = mapSceneSingleton.cloudsLayer;
      sprites.newIslandMarker = mapSceneSingleton.newIslandMarker;
      sprites.mapObjectHighlight = mapSceneSingleton.mapObjectHighlight;
      pixi.updateViewportSize = mapSceneSingleton.updateViewportSize;
      layers.island = mapSceneSingleton.islandLayer;

      this.attachCanvas();
      this.attachResizeListener();
      this.resumeApp();
      this.syncNewIslandMarkerListener();

      // Reload singleton textures that were cleaned on previous unmount
      (async () => {
        try {
          // Same single loader as first init. Marker builders await it themselves, so
          // this is only about repopulating scene.assets for other consumers.
          assets.plotMarkerTexture = await ensurePlotMarkerTexture();
          mapLoadedTexturePaths.add(PLOT_MARKER_TEXTURE_PATH);
        } catch (e) {
          console.error('[MapCanvas] Failed to reload plot marker texture:', e);
        }
        try {
          if (!mountState.isMounted || !layers.clouds) return;
          // If the clouds sprite survived (room round-trip keeps it alive), reuse it —
          // creating another would leave an orphaned invisible sprite leaking per visit.
          if (mapSceneSingleton.cloudsSprite && !mapSceneSingleton.cloudsSprite.destroyed) {
            sprites.clouds = mapSceneSingleton.cloudsSprite;
          } else {
            const cloudsTex = await PIXI.Assets.load(CLOUDS_OVERLAY_PATH);
            mapLoadedTexturePaths.add(CLOUDS_OVERLAY_PATH);
            const newCloudsSprite = new PIXI.Sprite(cloudsTex);
            newCloudsSprite.anchor.set(0.5);
            newCloudsSprite.position.set(0, 0);
            newCloudsSprite.width = WORLD_SIZE;
            newCloudsSprite.height = WORLD_SIZE;
            newCloudsSprite.alpha = 0;
            newCloudsSprite.eventMode = 'none';
            layers.clouds.addChild(newCloudsSprite);
            sprites.clouds = newCloudsSprite;
            mapSceneSingleton.cloudsSprite = newCloudsSprite;
          }
        } catch (e) {
          console.error('[MapCanvas] Failed to reload clouds texture:', e);
        }
        if (mountState.isMounted) {
          setIsPixiAppReady(true);
        }
      })();
    } else if (mapSceneSingleton.initPromise) {
      mapSceneSingleton.initPromise
        // This branch attaches to an init started by a previous mount. It used to mark
        // the scene ready WITHOUT reloading anything, even though a prior unmount frees
        // the map textures — the one path that could legitimately publish "ready" with a
        // null plot texture. Marker builders now await the texture themselves, but do not
        // rely on that alone: make readiness mean the texture really is there.
        .then(() => ensurePlotMarkerTexture().catch(() => null))
        .then(() => {
          if (!mountState.isMounted) return;
          this.attachCanvas();
          this.attachResizeListener();
          this.resumeApp();
          this.syncNewIslandMarkerListener();
          setIsPixiAppReady(true);
        })
        .catch(() => {});
    } else {
      const initWithRetry = async () => {
        const MAX_RETRIES = 3;
        for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
          try {
            await this.initApp(mountState);
            return; // success
          } catch (error) {
            console.error(`[MapCanvas] Init attempt ${attempt}/${MAX_RETRIES} failed:`, error);

            // cleanup failed attempt
            if (this.canvasEl) this.canvasEl.innerHTML = '';
            if (mapSceneSingleton.app) {
              try { mapSceneSingleton.app.destroy(true, true); } catch { /* destroy best-effort */ }
              mapSceneSingleton.app = null;
              mapSceneSingleton.viewport = null;
              mapSceneSingleton.mapObjectsLayer = null;
              mapSceneSingleton.highlightLayer = null;
              mapSceneSingleton.cloudsLayer = null;
              mapSceneSingleton.cloudsSprite = null;
              mapSceneSingleton.islandLayer = null;
            }

            if (attempt < MAX_RETRIES) {
              await new Promise(r => setTimeout(r, 1000 * attempt));
              continue;
            }

            // all retries exhausted
            Sentry.captureException(error, {
              tags: { component: 'MapCanvas', phase: 'initialization' },
              extra: { hasApp: !!mapSceneSingleton.app, hasCanvas: !!this.canvasEl, attempt }
            });
            mapSceneSingleton.isReady = false;
            mapSceneSingleton.initPromise = null;
            showAlert('Помилка завантаження мапи. Спробуйте оновити сторінку.');
          }
        }
      };
      mapSceneSingleton.initPromise = initWithRetry();
    }
  }

  unmount(): void {
    const { setLastRenderKey } = this.deps;

    if (this.activeMount) this.activeMount.isMounted = false;
    if (mapSceneSingleton.updateViewportSize && mapSceneSingleton.isResizeListenerAttached) {
      window.removeEventListener('resize', mapSceneSingleton.updateViewportSize);
      mapSceneSingleton.isResizeListenerAttached = false;
    }

    // When entering a room interior MapCanvas unmounts but the room scene reuses
    // the same singleton PIXI app and merely HIDES the map viewport (RoomCanvas
    // re-shows it on its own unmount). City Hall is a pure DOM overlay route that
    // leaves the (paused) map app untouched underneath. In both cases keep the
    // markers + textures alive so returning to the map skips the full
    // destroy+rebuild — the persisted render key lets the main effect
    // short-circuit. Only do the expensive teardown on a real exit (e.g. tap).
    const goingToRoom = useInterfaceStore.getState().currentView === 'room';
    const goingToCityHall = window.location.pathname.startsWith('/cityhall');
    if (goingToRoom || goingToCityHall) {
      console.warn('[MapCanvas] CLEANUP (→ room/cityhall): keeping markers alive, skipping rebuild on return');
      this.pauseApp();
      return;
    }

    console.warn('[MapCanvas] INIT EFFECT CLEANUP running — component is unmounting!');
    this.clearMapMarkers();

    // Free all map textures from GPU, CPU, and caches
    this.cleanupMapTextures();

    // Null out singleton texture refs so remount reloads them.
    // The promise must go too: it resolves to the texture we just destroyed, so a
    // marker awaiting it would swap in a dead texture. Both init paths re-publish it
    // before setIsPixiAppReady(true), and every marker build is gated on that flag,
    // so no marker can be created while both are null.
    mapSceneSingleton.plotMarkerTexture = null;
    mapSceneSingleton.plotMarkerTexturePromise = null;
    if (mapSceneSingleton.cloudsSprite) {
      if (mapSceneSingleton.cloudsSprite.parent) {
        mapSceneSingleton.cloudsSprite.parent.removeChild(mapSceneSingleton.cloudsSprite);
      }
      mapSceneSingleton.cloudsSprite.destroy();
      mapSceneSingleton.cloudsSprite = null;
    }

    // Reset render key so the map objects effect rebuilds on remount
    setLastRenderKey(null);

    this.resetMapTrackingState();

    this.pauseApp();
  }
}
